import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import OverviewTab from "../Tabs/overviewTab";
import ContentTab from "../Tabs/contentTab";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../@/components/ui/tabs";
import { EditCourseDialog } from "../forms/editCourse";
import { AddTopicDialog } from "../forms/addTopic";
import StudentsTab from "../Tabs/studentsTab";
import { Button } from "../../@/components/ui/button";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/emptyState";
import { BookOpenIcon } from "lucide-react";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { Spinner } from "@/components/spinner";
import EnrollStudents from "@/forms/enrollStudents";
import DeleteCourseDialog from "@/forms/deleteCourse";
import { toast } from "sonner";
import MakeAnnouncementDialog from "@/forms/makeAnnouncementDialog";
import CourseBulkEnrollDialog from "@/forms/courseBulkEnrollDialog";

import { getStudentAppBaseUrlForCurrentHost } from "@/lib/studentAppUrl";
import { getClerkToken } from "@/lib/clerkToken";
import { apiRequest } from "@/lib/apiRequest";
import { queryKeys } from "@/lib/queryKeys";

import EnrolledCard from "@/components/StatCards/enrolledCard";
import CompletedCard from "@/components/StatCards/completedCard";
import StepsCard from "@/components/StatCards/stepsCard";
import RatingCard from "@/components/StatCards/ratingCard";

export default function CourseManagementPage() {
  const location = useLocation();
  const { state } = location;
  // Rely on nearest match to avoid mismatched `from` strings
  const { courseId } = useParams({ strict: false });
  const navigate = useNavigate();
  // Debugging: show incoming router state and params
  useEffect(() => {
    console.log("CourseManagementPage mounted", { state, courseId });
  }, [state, courseId]);
  const [error, setError] = useState(null);
  const [course, setCourse] = useState(state?.course);
  // Initialize active tab from navigation state or URL query param
  const urlTab =
    typeof window !== "undefined"
      ? new URLSearchParams(location.search).get("tab")
      : null;
  const initialTab = state?.tab || urlTab || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [shouldRenderStudentsTab, setShouldRenderStudentsTab] = useState(
    initialTab === "students"
  );

  useEffect(() => {
    if (activeTab === "students") setShouldRenderStudentsTab(true);
  }, [activeTab]);

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentFilter, setStudentFilter] = useState("all");
  const [enrolledStudentIds, setEnrolledStudentIds] = useState([]);
  const [studentsRefreshKey, setStudentsRefreshKey] = useState(0);

  const [courseLoading, setCourseLoading] = useState(!state?.course);
  const [contentLoading, setContentLoading] = useState(false);

  const [topics, setTopics] = useState(null);
  const [overviewRefreshKey, setOverviewRefreshKey] = useState(0);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const { getToken, isLoaded } = useAuth();
  const queryClient = useQueryClient();

  const courseIdentifier = useMemo(() => {
    const fromState = state?.course?.slug;
    const fromParam = courseId;
    const fromStorage =
      typeof window !== "undefined"
        ? localStorage.getItem("currentCourseSlug")
        : null;
    return fromState || fromParam || fromStorage || null;
  }, [state, courseId]);

  const identifierMeta = useMemo(() => {
    const raw = courseIdentifier ? String(courseIdentifier) : "";
    const isNumericId = Boolean(raw) && /^\d+$/.test(raw);
    return { raw, isNumericId };
  }, [courseIdentifier]);

  const courseQuery = useQuery({
    queryKey: identifierMeta.isNumericId
      ? queryKeys.course.byId(identifierMeta.raw)
      : queryKeys.course.bySlug(identifierMeta.raw),
    enabled: Boolean(isLoaded && identifierMeta.raw),
    queryFn: async ({ signal }) => {
      const path = identifierMeta.isNumericId
        ? `/course/${identifierMeta.raw}`
        : `/course/slug/${identifierMeta.raw}`;
      const data = await apiRequest(getToken, {
        path,
        auth: "required",
        signal,
      });
      return data?.course ?? null;
    },
    initialData: state?.course ?? undefined,
  });

  const topicsQuery = useQuery({
    queryKey: queryKeys.course.topicsWithContent(course?.id),
    enabled: Boolean(isLoaded && course?.id),
    queryFn: async ({ signal }) => {
      const topicsPath = `/topic/${course.id}`;
      if (import.meta.env.DEV) {
        console.log("[ContentTab][topicsQuery] GET", {
          path: topicsPath,
          courseId: course.id,
          queryKey: queryKeys.course.topicsWithContent(course?.id),
        });
      }

      try {
        const topicData = await apiRequest(getToken, {
          path: topicsPath,
          auth: "required",
          signal,
        });

        const topicsList = topicData?.topics || [];
        if (import.meta.env.DEV) {
          console.log("[ContentTab][topicsQuery] Topics response", {
            path: topicsPath,
            topicsCount: topicsList.length,
            raw: topicData,
          });
        }

        return await Promise.all(
          topicsList.map(async (topic) => {
            const lessonsPath = `/lesson/topic/${topic.id}`;
            const projectsPath = `/project/topic/${topic.id}`;
            const quizzesPath = `/quiz/topic/${topic.id}`;

            if (import.meta.env.DEV) {
              console.log("[ContentTab][topic-content] GET", {
                topicId: topic.id,
                topicName: topic?.name ?? topic?.title ?? null,
                lessonsPath,
                projectsPath,
                quizzesPath,
              });
            }

            const [lessonsData, projectsData, quizzesData] = await Promise.all([
              apiRequest(getToken, {
                path: lessonsPath,
                auth: "required",
                signal,
              }),
              apiRequest(getToken, {
                path: projectsPath,
                auth: "required",
                signal,
              }),
              apiRequest(getToken, {
                path: quizzesPath,
                auth: "required",
                signal,
              }),
            ]);

            if (import.meta.env.DEV) {
              console.log("[ContentTab][topic-content] Response", {
                topicId: topic.id,
                lessonsCount: Array.isArray(lessonsData?.lessons)
                  ? lessonsData.lessons.length
                  : 0,
                projectsCount: Array.isArray(projectsData?.projects)
                  ? projectsData.projects.length
                  : 0,
                quizzesCount: Array.isArray(quizzesData?.quizzes)
                  ? quizzesData.quizzes.length
                  : 0,
                lessonsData,
                projectsData,
                quizzesData,
              });
            }

            return {
              ...topic,
              lessons: lessonsData?.lessons || [],
              quizzes: quizzesData?.quizzes || [],
              projects: projectsData?.projects || [],
            };
          })
        );
      } catch (error) {
        const isCanceled =
          error?.code === "ERR_CANCELED" ||
          error?.name === "CanceledError" ||
          error?.name === "AbortError";

        if (import.meta.env.DEV) {
          const log = isCanceled ? console.debug : console.error;
          log("[ContentTab][topicsQuery] Failed", {
            path: topicsPath,
            courseId: course.id,
            canceled: isCanceled,
            errorName: error?.name,
            errorCode: error?.code,
            errorMessage: error?.message,
            responseData: error?.response?.data,
          });
        }
        throw error;
      }
    },
  });


  const completedStudentsQuery = useQuery({
    queryKey: queryKeys.course.completedStudents(course?.id),
    enabled: Boolean(isLoaded && course?.id),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: `/course/${course.id}/completed-students`,
        auth: "required",
        signal,
      }),
  });

  const reviewsQuery = useQuery({
    queryKey: queryKeys.course.reviews(course?.id),
    enabled: Boolean(isLoaded && course?.id),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: `/course/reviews/${course.id}`,
        auth: "required",
        signal,
      }),
  });

  

  useEffect(() => {
    if (courseQuery.data && courseQuery.data !== course) {
      setCourse(courseQuery.data);
    }
    if (courseQuery.error) {
      setError(
        courseQuery.error?.response?.data?.detail ||
          courseQuery.error?.message ||
          "Failed to fetch course details"
      );
    }
  }, [courseQuery.data, courseQuery.error]);

  useEffect(() => {
    if (topicsQuery.data) {
      setTopics(topicsQuery.data);
    }
  }, [topicsQuery.data]);

  useEffect(() => {
    if (course?.slug) {
      try {
        localStorage.setItem("currentCourseSlug", course.slug);
      } catch {
        // ignore
      }
    }
  }, [course?.slug]);

  useEffect(() => {
    if (!isLoaded || !course?.id) return;
    const id = course.id;

    queryClient.prefetchQuery({
      queryKey: queryKeys.course.students(id),
      queryFn: ({ signal }) =>
        apiRequest(getToken, {
          path: `/course/${id}/students`,
          auth: "required",
          signal,
          retryOnceOn401: true,
        }),
    });
    queryClient.prefetchQuery({
      queryKey: queryKeys.course.completedStudents(id),
      queryFn: ({ signal }) =>
        apiRequest(getToken, {
          path: `/course/${id}/completed-students`,
          auth: "required",
          signal,
        }),
    });
    queryClient.prefetchQuery({
      queryKey: queryKeys.course.pathways(id),
      queryFn: ({ signal }) =>
        apiRequest(getToken, {
          path: `/course/pathways/${id}`,
          auth: "required",
          signal,
        }),
    });
    queryClient.prefetchQuery({
      queryKey: queryKeys.course.instructors(id),
      queryFn: ({ signal }) =>
        apiRequest(getToken, {
          path: `/course/${id}/instructors`,
          auth: "required",
          signal,
        }),
    });
    queryClient.prefetchQuery({
      queryKey: queryKeys.course.reviews(id),
      queryFn: ({ signal }) =>
        apiRequest(getToken, {
          path: `/course/reviews/${id}`,
          auth: "required",
          signal,
        }),
    });
  }, [course?.id, getToken, isLoaded, queryClient]);

  const handlePreviewCourse = () => {
    if (!course?.slug) return;
    const studentAppUrl = getStudentAppBaseUrlForCurrentHost();
    window.open(
      `${studentAppUrl}/course/${course.slug}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const isPublished = course?.status === 1;

  const handlePublishToggle = async () => {
    if (!course?.id) return;

    try {
      setIsUpdatingStatus(true);
      const token = await getClerkToken(getToken);
      if (!token) return;

      const newStatus = isPublished ? 0 : 1;
      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(
        api_endpoint + `/course/update/status/${course.id}/${newStatus}`,
        {},
        reqConfig
      );

      const updatedCourse = { ...course, status: newStatus };
      setCourse(updatedCourse);
      if (typeof handleCourseUpdated === "function") {
        await handleCourseUpdated(updatedCourse);
      }

      toast.success(
        `Course ${newStatus === 1 ? "published" : "unpublished"} successfully!`
      );
    } catch (error) {
      console.error("Error updating course status:", error);
      toast.error("Failed to update course status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Central handler to change tab and sync `?tab=` in the URL
  const handleTabChange = (tab, filter = "all", replace = true) => {
    setActiveTab(tab);
    if (tab === "students") setStudentFilter(filter);
    try {
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      navigate({ to: `${location.pathname}?${params.toString()}`, replace });
    } catch (e) {
      console.warn("Failed to update tab in URL", e);
    }
  };

  const showCompletedOnly = () => {
    // use central handler so URL is updated too
    handleTabChange("students", "COMPLETED");
  };

  const handleSwitchTab = (tab, filter = "all") => {
    handleTabChange(tab, filter);
  };

  const handleCourseDeleted = (courseId) => {
    console.log(`Course ${courseId} deleted`);
  };

  const handleRedirectToManageCourses = () => {
    localStorage.removeItem("currentCourseSlug");
    navigate({ to: "/manage-courses" });
  };

  // Fetch all topics, lessons, quizzes, and projects for the course
  const loadAllTopicsData = async () => {
    setContentLoading(true);
    setError(null);
    try {
      const token = await getClerkToken(getToken);
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const topicResponse = await axios.get(
        api_endpoint + `/topic/${course.id}`,
        reqConfig
      );
      console.log("My Topics:", topicResponse);
      const topicsList = topicResponse.data.topics || [];

      const topicsWithLessons = [];
      for (const topic of topicsList) {
        const lessonsResponse = await axios.get(
          api_endpoint + `/lesson/topic/${topic.id}`,
          reqConfig
        );
        console.log("Lessons for topic", topic.id, lessonsResponse);
        const projectResponse = await axios.get(
          api_endpoint + `/project/topic/${topic.id}`,
          reqConfig
        );
        const quizzesResponse = await axios.get(
          api_endpoint + `/quiz/topic/${topic.id}`,
          reqConfig
        );

        topicsWithLessons.push({
          ...topic,
          lessons: lessonsResponse.data.lessons || [],
          quizzes: quizzesResponse.data.quizzes || [],
          projects: projectResponse.data.projects || [],
        });
      }

      setTopics(topicsWithLessons);
    } catch (error) {
      console.error(error);
      setError("Failed to load content.");
    } finally {
      setContentLoading(false);
    }
  };

  // Handler for when a topic is added
  const handleTopicAdded = (newTopic) => {
    console.log("New topic added:", newTopic);
    // Refetch via React Query cache
    queryClient.invalidateQueries({
      queryKey: queryKeys.course.topicsWithContent(course?.id),
    });
  };

  // Function to handle selected students from StudentsTab
  const handleSelectedStudentsChange = (students) => {
    setSelectedStudents(students);
  };

  const fetchCourseDetails = useCallback(
    async (identifier) => {
      // identifier can be a slug (string with letters/hyphens) or a numeric id
      console.log("Fetching details for course identifier:", identifier);
      setCourseLoading(true);
      setError(null);

      try {
        const token = await getClerkToken(getToken);
        console.log("getToken result for fetchCourseDetails", {
          token: !!token,
        });
        const reqConfig = {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };

        const raw = identifier ? String(identifier) : "";
        const isNumericId = Boolean(raw) && /^\d+$/.test(raw);
        const endpoint = isNumericId
          ? `${api_endpoint}/course/${raw}`
          : `${api_endpoint}/course/slug/${raw}`;
        const response = await axios.get(endpoint, reqConfig);
        console.log("Course detail endpoint:", endpoint)
        console.log("fetchCourseDetails response", response?.data);
        if (!response?.data?.course) {
          setCourse(null);
          localStorage.removeItem("currentCourseSlug");
          setError("Course not found (API returned no course)");
          return null;
        }
        setCourse(response.data.course);
        // Save slug to localStorage for persistence on page refresh
        localStorage.setItem("currentCourseSlug", response.data.course.slug);
        return response.data.course;
      } catch (error) {
        console.error(
          "Failed to fetch course details:",
          error,
          error?.response?.data
        );
        setCourse(null);
        setError(
          error?.response?.data?.detail || "Failed to fetch course details"
        );
        
      } finally {
        setCourseLoading(false);
      }
    },
    [getToken]
  );

  const handleCourseUpdated = async (updatedCourse) => {
    if (!updatedCourse?.slug && !updatedCourse?.id) return;
    setCourse(updatedCourse);
    try {
      if (updatedCourse?.slug) {
        localStorage.setItem("currentCourseSlug", updatedCourse.slug);
      }
    } catch {
      // ignore
    }
    if (updatedCourse?.slug) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.bySlug(updatedCourse.slug),
      });
    }
    if (updatedCourse?.id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.byId(updatedCourse.id),
      });
    }
    if (updatedCourse?.id) {
      const id = updatedCourse.id;
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.topicsWithContent(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.course.students(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.completedStudents(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.course.pathways(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.course.instructors(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.course.reviews(id) });
    }
    setTimeout(() => {
      setOverviewRefreshKey((prev) => prev + 1);
    }, 250);
  };

   // ✅ Show loading UI while fetching course (prevents "Course not found" flash)
  if (!isLoaded || courseQuery.isLoading) {
    return (
      <EmptyState
        // icon={BookOpenIcon}
        title={<Spinner />}
        // description="Fetching course details..."
      />
    );
  }

  if (error) {
    return <EmptyState icon={BookOpenIcon} title="Error" description={error} />;
  }

  if (!course) {
    return (
      <EmptyState
        icon={BookOpenIcon}
        title="Course not found"
        description="The course you are looking for does not exist or you don't have access to it."
      />
    );
  }

  const handleAnnouncementSubmit = (payload) => {
    console.log("Announcement payload", payload);
    toast.success("Announcement ready to send");
  };

  const renderTabActions = () => {
    const recipients = activeTab === "students" ? selectedStudents : [];
    const makeAnnouncementButton = (
      <MakeAnnouncementDialog
        mode="course"
        courseId={course.id}
        selectedCount={recipients.length}
        onSubmit={(announcement) =>
          handleAnnouncementSubmit({
            ...announcement,
            recipientIds: recipients,
            courseId: course.id,
          })
        }
        courseTitle={course.title}
        triggerVariant="outline"
        triggerClassName="w-full md:w-auto border-(--color-primary) text-(--color-primary) hover:border-(--color-accent) hover:text-(--color-accent) rounded-md"
      />
    );

    let primaryAction = null;
    let dropdownContent = null;

    if (activeTab === "overview") {
      primaryAction = (
        <EditCourseDialog
          course={course}
          onCourseUpdated={handleCourseUpdated}
          className="flex-1 sm:flex-none w-full sm:w-auto rounded-l-md rounded-r-none bg-(--color-primary) text-white hover:bg-(--color-accent) border border-(--color-primary) flex items-center justify-center gap-2"
          refreshKey={overviewRefreshKey}
          setRefreshKey={setOverviewRefreshKey}
        />
      );
      dropdownContent = (
        <DropdownMenuContent align="end" className="min-w-50">
          <DropdownMenuItem
            disabled={isUpdatingStatus}
            onSelect={() => {
              handlePublishToggle();
            }}
            className="hover:text-(--color-accent)"
          >
            {isUpdatingStatus
              ? isPublished
                ? "Unpublishing…"
                : "Publishing…"
              : isPublished
                ? "Unpublish Course"
                : "Publish Course"}
          </DropdownMenuItem>
          <DeleteCourseDialog
            course={course}
            onDeleted={handleCourseDeleted}
            onRedirect={handleRedirectToManageCourses}
            renderTrigger={({ open }) => (
              <DropdownMenuItem
                variant=""
                onSelect={(event) => {
                  event.preventDefault();
                  open();
                }}
                className="text-(--color-primary) hover:text-(--color-accent)"
              >
                Delete Course
              </DropdownMenuItem>
            )}
          />
        </DropdownMenuContent>
      );
    } else if (activeTab === "content") {
      primaryAction = (
        <AddTopicDialog
          courseId={course.id}
          onTopicAdded={handleTopicAdded}
          refetchTopics={() => topicsQuery.refetch()}
          triggerLabel="Add Topic"
          triggerClassName="flex-1 sm:flex-none w-full sm:w-auto rounded-md bg-(--color-primary) text-white hover:bg-(--color-accent) border border-(--color-primary)"
        />
      );
      // Add Topic should be a single button (no dropdown trigger).
      return (
        <div className="flex flex-col gap-2 w-full md:w-auto md:flex-row md:items-center md:gap-3 md:justify-end">
          <div className="w-full md:w-auto">{makeAnnouncementButton}</div>
          <div className="w-full md:w-auto">{primaryAction}</div>
        </div>
      );
    } else if (activeTab === "students") {
      primaryAction = (
        <EnrollStudents
          courseId={course.id}
          onEnrolled={() => {
            setStudentsRefreshKey((prev) => prev + 1);
            queryClient.invalidateQueries({
              queryKey: queryKeys.course.students(course.id),
            });
          }}
          enrolledStudentIds={enrolledStudentIds}
          triggerLabel="New Enrollment "
          triggerVariant="default"
          triggerClassName="flex-1 sm:flex-none w-full sm:w-auto rounded-l-md rounded-r-none bg-(--color-primary) text-white hover:bg-(--color-accent) border border-(--color-primary)"
        />
      );
      dropdownContent = (
        <DropdownMenuContent align="end" className="min-w-50">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              setBulkDialogOpen(true);
            }}
          >
            Bulk Enroll Students
          </DropdownMenuItem>
        </DropdownMenuContent>
      );
    } else {
      return makeAnnouncementButton;
    }

    const menuContent = dropdownContent ?? (
      <DropdownMenuContent align="end" className="min-w-50" />
    );

    return (
      <div className="flex flex-col gap-2 w-full md:w-auto md:flex-row md:items-center md:gap-3 md:justify-end">
        <div className="w-full md:w-auto">{makeAnnouncementButton}</div>
        <div className="flex w-full md:w-auto items-stretch gap-0.5">
          {primaryAction}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="default"
                className="flex-none w-12 md:w-auto rounded-l-none rounded-r-md border border-(--color-primary) bg-(--color-primary) text-white px-0 md:px-3 flex items-center justify-center"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            {menuContent}
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const completedStudentsList =
    completedStudentsQuery.data?.completed_students ||
    completedStudentsQuery.data?.completedStudents ||
    [];
  const completedStudentsCount = Array.isArray(completedStudentsList)
    ? completedStudentsList.length
    : 0;

  const totalStudentsCount = Number(course?.total_students ?? 0) || 0;
  const averageRatingRaw = Number(course?.average_rating ?? 0) || 0;
  const reviewsList = reviewsQuery.data?.reviews ?? [];
  const totalReviewsCount =
    Number(course?.total_reviews ?? (Array.isArray(reviewsList) ? reviewsList.length : 0)) ||
    0;

  const topicsForCount = topicsQuery.data ?? topics;
  const stepsCount = Array.isArray(topicsForCount)
    ? topicsForCount.length
    : Number(course?.topics ?? 0) || 0;

  const handleViewReviews = () => {
    handleTabChange("overview");
    // Allow the overview tab to render before scrolling.
    setTimeout(() => {
      const el = document.getElementById("reviewsSection");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  return (
    <div className="flex flex-col w-full bg-(--surface-base) text-(--neutral-900)">
      <h1 className="">{course.title}</h1>
      <p className="font-body">{course.summary}</p>

      <div className="mt-2 mb-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <EnrolledCard count={totalStudentsCount} onSwitchTab={handleSwitchTab} />
        <CompletedCard
          count={completedStudentsCount}
          onShowCompleted={showCompletedOnly}
          totalStudents={totalStudentsCount}
          completedStudentsList={completedStudentsList}
        />
        <StepsCard count={stepsCount} onSwitchTab={handleSwitchTab} />
        <RatingCard
          rating={averageRatingRaw}
          reviews={totalReviewsCount}
          onActionClick={handleViewReviews}
        />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => handleTabChange(value)}
        className="space-y-4 mt-3"
      >
        <div className="flex flex-col w-full gap-3 md:flex-row md:items-center">
          <TabsList className="w-full md:w-auto">
            <TabsTrigger
              value="overview"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="content"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Content
            </TabsTrigger>
            <TabsTrigger
              value="students"
              className="w-full md:w-auto px-3 py-0.5 text-sm"
            >
              Students
            </TabsTrigger>
          </TabsList>
          <div className="w-full md:w-auto md:ml-auto">
            {renderTabActions()}
          </div>
        </div>
        <TabsContent value="overview">
          <OverviewTab
            course={course}
            onSwitchTab={handleSwitchTab}
            onPreviewCourse={handlePreviewCourse}
            onCourseUpdated={handleCourseUpdated}
            refreshKey={overviewRefreshKey}
            loading={courseQuery.isLoading}
          />
        </TabsContent>
        <TabsContent value="content">
          <ContentTab
            course={course}
            topics={topicsQuery.data ?? topics}
            loading={topicsQuery.isLoading}
            error={
              topicsQuery.error?.response?.data?.detail ||
              topicsQuery.error?.message ||
              error
            }
            refetchTopics={() => topicsQuery.refetch()}
          />
        </TabsContent>
        <TabsContent value="students" forceMount>
          {!shouldRenderStudentsTab ? null : (
            <StudentsTab
              course={course}
              onSelectedStudentsChange={handleSelectedStudentsChange}
              filterStatus={studentFilter}
              onEnrolledIdsChange={setEnrolledStudentIds}
              refreshKey={studentsRefreshKey}
            />
          )}
        </TabsContent>
      </Tabs>
      <CourseBulkEnrollDialog
        courseId={course.id}
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        enrolledStudentIds={enrolledStudentIds}
        onUploaded={() => {
          setStudentsRefreshKey((prev) => prev + 1);
          if (course?.id) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.course.students(course.id),
            });
          }
        }}
      />
    </div>
  );
}
